import {useAtom} from "jotai"
import {type AnalysisOptions, AnalysisOptionsAtom} from "./AnalysisOptions.ts";
import {resolveSetterVal, type Setter, type SetterVal} from "../../util/setter/Setter.ts";
import {ProjectOptionsBlock} from "./project-selection/ProjectOptionsBlock.tsx";
import {useFieldSetter} from "../../util/setter/FieldSetter.ts";
import {Field} from "@matillion/component-library";
import type {ChangeEvent} from "react";
import {CheckboxField} from "../../components/checkbox-field/CheckboxField.tsx";
import {JobStrategyOptions} from "./JobStrategyOptions.tsx";
import {PromiseRunnerOptionsWidget} from "./PromiseRunnerOptionsWidget.tsx";
import {OptionalScanOptionsWidget} from "./OptionalScanOptionsWidget.tsx";

function TaskHistoryOptions({options, setOptions}: {options: AnalysisOptions["analyseTaskHistory"], setOptions: Setter<AnalysisOptions["analyseTaskHistory"]>}) {
    const [enabled, setEnabled] = useFieldSetter(options, setOptions, "enabled")
    const [durationToQueryInDays, setDurationToQueryInDays] = useFieldSetter(options, setOptions, "durationToQueryInDays")
    const [taskQueryDurationHours, setTaskQueryDurationHours] = useFieldSetter(options, setOptions, "taskQueryDurationHours")
    const [runner, setRunner] = useFieldSetter(options, setOptions, "promiseRunner")
    return <div>
        <h3>Task History Scan</h3>
        <CheckboxField title="Enabled"
                       description="Scan task history. We recommend enabling this so that a better picture can be gained of usage patterns"
                       value={enabled}
                       setValue={setEnabled}
                       small
                       />
        <Field title="Duration to query in days"
               description="The total number of days to scan the task history of"
               disabled={!enabled}
               type="number" value={durationToQueryInDays} onChange={(newValue: ChangeEvent<HTMLInputElement>) => setDurationToQueryInDays(newValue.target.valueAsNumber)} />
        <Field title="Task query duration hours"
               description="The number of hours to query in a single API request"
               disabled={!enabled}
               type="number" value={taskQueryDurationHours} onChange={(newValue: ChangeEvent<HTMLInputElement>) => setTaskQueryDurationHours(newValue.target.valueAsNumber)} />
        <PromiseRunnerOptionsWidget title={"Task Download Runner"}
                                    disabled={!enabled}
                                    description="Whether to download task history blocks in series, or in parallel"
                                    options={runner} setOptions={setRunner} />
    </div>
}

export function OptionSelectionWidget() {
    const [options, setOptions] = useAtom(AnalysisOptionsAtom)
    const safeSetOptions = (newOptions: SetterVal<AnalysisOptions>) => {
        setOptions((oldValue) => {
            const newVal = resolveSetterVal(oldValue, newOptions)
            if(newVal !== oldValue) {
                return {...newVal, id: Math.random().toString(36).slice(2, 9)}
            }
            return oldValue
        })
    }
    const [analyseDrivers, setAnalyseDrivers] = useFieldSetter(options, safeSetOptions, "analyseDrivers")
    const [analyseSchedules, setAnalyseSchedules] = useFieldSetter(options, safeSetOptions, "analyseSchedules")
    const [analyseSharedJobs, setAnalyseSharedJobs] = useFieldSetter(options, safeSetOptions, "analyseSharedJobs")
    const [sharedJobRunner, setSharedJobRunner] = useFieldSetter(options, safeSetOptions, "sharedJobRunner")
    const [taskHistory, setTaskHistory] = useFieldSetter(options, safeSetOptions, "analyseTaskHistory")
    const [sharedErrors, setSharedErrors] = useFieldSetter(options, safeSetOptions, "reportAnonymisedErrors")
    return <div>
        <h2>Option Selection</h2>
        <div>
            <h3>Projects</h3>
            <ProjectOptionsBlock options={options} setOptions={safeSetOptions} />
        </div>
        <hr />
        <div>
            <TaskHistoryOptions options={taskHistory} setOptions={setTaskHistory} />
        </div>
        <hr />
        <div>
            <h3>Options</h3>
            <CheckboxField title="Automatically Report Errors"
                           description="Automatically report errors to help us improve our tool"
                           small
                           value={sharedErrors} setValue={setSharedErrors} />
        </div>
        <hr />
        <div>
            <h3>Extra Analysis</h3>
            <CheckboxField title="Analyse Drivers"
                   description="Scan driver groups. This will only scan the list of drivers uploaded, not the files themselves."
                   small
                   value={analyseDrivers} setValue={setAnalyseDrivers} />
            <CheckboxField title="Analyse Schedules"
                   description="Scan the list of schedules in scanned projects. This gives us a better picture of discrete workloads."
                   small
                   value={analyseSchedules} setValue={setAnalyseSchedules} />
            <CheckboxField title="Analyse Shared Jobs"
                   description="Analyse the jobs underlying the shared jobs on this instance. Helps to give a full picture of any hidden complexity"
                   small
                   value={analyseSharedJobs} setValue={setAnalyseSharedJobs} />
            <PromiseRunnerOptionsWidget
                title={"Shared Job Runner"}
                description={"Whether to download shared jobs in series, or in parallel"}
                options={sharedJobRunner} setOptions={setSharedJobRunner} disabled={!analyseSharedJobs} />
        </div>
        <hr />
        <div>
            <JobStrategyOptions options={options} setOptions={safeSetOptions} />
        </div>
        <hr />
        <OptionalScanOptionsWidget options={options} setOptions={safeSetOptions} />
    </div>
}